/**
 * «Смена салона»: по дням — очередь утро→вечер, сделано, часы, опер-конверсия;
 * ниже сотрудники с долями клиентов и задач. Спека
 * `docs/specs/2026-09-25-salon-shift-design.md`; числа — те же, что у
 * `prokat-runner salon-shift`.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router";

import { lastWeek } from "@/lib/idle";
import { type Queue, type Salon, loadSalonShift, operClass } from "@/lib/salon";
import { PeriodBar } from "@/pages/IdlePage";

const ALL = 0;
const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const wd = (day: string) => WD[new Date(`${day}T12:00:00`).getDay()];

function useSalonParams() {
  const [params, setParams] = useSearchParams();
  const def = lastWeek();
  const p = {
    from: params.get("from") || def.from,
    to: params.get("to") || def.to,
    location: Number(params.get("location")) || ALL,
  };
  const set = (next: Partial<typeof p>) => {
    const v = { ...p, ...next };
    setParams({ from: v.from, to: v.to, location: String(v.location) }, { replace: true });
  };
  return [p, set] as const;
}

const oper = (v: number | null) => <span className={operClass(v)}>{v === null ? "—" : `${v}%`}</span>;
const pct = (v: number | null) => (v === null ? <span className="text-fg-faint">—</span> : `${v}%`);

function queue(q: Queue | null): ReactNode {
  if (!q || q.morning === null) return <span className="text-fg-faint">—</span>;
  const d = q.delta ?? 0;
  return (
    <>
      {q.morning} → {q.evening}{" "}
      <span className={d < 0 ? "text-ok" : d > 0 ? "text-err" : "text-fg-faint"}>({d > 0 ? `+${d}` : d})</span>
    </>
  );
}

const th = "cursor-help whitespace-nowrap px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2";
const td = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";

function Head({ cols }: { cols: [string, string][] }) {
  return (
    <thead className="text-left text-[11.5px] text-fg-faint">
      <tr>
        {cols.map(([h, hint], i) => (
          <th key={h} title={hint} className={i === 0 ? "px-4 py-2 text-left font-medium" : th} data-annot={`колонка «${h}»`}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}

const DAY_COLS: [string, string][] = [
  ["День", ""],
  ["Перемещения", "открытые задачи перемещения салона: первый → последний часовой снимок CRM (sStat_state_moveTask)"],
  ["Сбор", "не собрано под брони сегодня…+10 дней: первый → последний снимок prokat (с 25.09.26)"],
  ["Сделано", "задач перемещения выполнено сотрудниками салона (sMove_task, doneUserID)"],
  ["Людей", "сотрудников с оплаченным временем"],
  ["Оплачено", "часы: акцепт, если есть, иначе статусы (sUser_time_result)"],
  ["Клиенты", "доля оплаченного времени на примерках, приёме и выдаче"],
  ["Задачи", "доля оплаченного времени в нормативных минутах задач"],
  ["Опер-конв.", "задачи / (оплачено − клиенты), только стилисты, комплектовщики, руководители; как statTotal"],
];

const USER_COLS: [string, string][] = [
  ["Сотрудник", ""],
  ["Роль", "sUser.userRoleID"],
  ["Дней", "дней с оплаченным временем"],
  ["Оплачено", "часы за период"],
  ["Клиенты", "доля оплаченного на клиентов"],
  ["Задачи", "доля оплаченного в минутах задач"],
  ["Опер-конв.", "задачи / (оплачено − клиенты) по самому сотруднику"],
  ["Сделано", "задач перемещения выполнено"],
];

function SalonCard({ s, open }: { s: Salon; open: boolean }) {
  const t = s.total;
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`салон ${s.short}`}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 pt-3 text-[13px]">
        <span className="font-semibold">{s.name}</span>
        {s.marketplace && (
          <span className="rounded bg-bg-hover px-1.5 text-[11px] text-fg-muted" title="много задач маркетплейса — опер-конверсию читать с поправкой">
            маркетплейс
          </span>
        )}
        <span className="text-fg-muted">
          оплачено {t.paid_h} ч · клиенты {pct(t.customer_pct)} · задачи {pct(t.task_pct)} · сделано {t.tasks_done} · опер-конв.{" "}
          <b>{oper(t.opconv)}</b>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <Head cols={DAY_COLS} />
          <tbody>
            {s.days.map((d) => (
              <tr
                key={d.date}
                className="border-t border-border"
                data-annot={`день ${d.date}`}
                title={d.roles.map((r) => `${r.role}: ${r.paid_h} ч, клиенты ${r.customer_pct ?? "—"}%, задачи ${r.task_pct ?? "—"}%`).join("\n")}
              >
                <td className="whitespace-nowrap px-4 py-1.5">
                  {dm(d.date)} <span className="text-fg-faint">{wd(d.date)}</span>
                </td>
                <td className={td}>{queue(d.move_queue)}</td>
                <td className={td}>{queue(d.sbor_queue)}</td>
                <td className={td}>{d.tasks_done}</td>
                <td className={td}>{d.people}</td>
                <td className={td}>{d.paid_h}</td>
                <td className={td}>{pct(d.customer_pct)}</td>
                <td className={td}>{pct(d.task_pct)}</td>
                <td className={td}>
                  <b>{oper(d.opconv)}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details open={open} className="border-t border-border" data-annot="сотрудники салона">
        <summary className="cursor-pointer px-4 py-2 text-[12.5px] text-fg-muted">Сотрудники ({s.users.length})</summary>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <Head cols={USER_COLS} />
            <tbody>
              {s.users.map((u) => (
                <tr key={u.user_id} className="border-t border-border" data-annot={`сотрудник ${u.user_id}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">{u.name}</td>
                  <td className={td}>{u.role}</td>
                  <td className={td}>{u.days}</td>
                  <td className={td}>{u.paid_h}</td>
                  <td className={td}>{pct(u.customer_pct)}</td>
                  <td className={td}>{pct(u.task_pct)}</td>
                  <td className={td}>
                    <b>{oper(u.opconv)}</b>
                  </td>
                  <td className={td}>{u.tasks_done}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

export function SalonPage() {
  const [p, set] = useSalonParams();
  const q = useQuery({ queryKey: ["salon-shift", p.from, p.to, p.location], queryFn: () => loadSalonShift(p) });
  const locations = [{ id: ALL, name: "Все салоны" }, ...(q.data?.locations ?? [])];
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <PeriodBar title="Смена салона" p={p} set={set} locations={locations} />
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю очередь, часы и опер-конверсию по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить смену салона: {String(q.error.message)}</div>
      ) : (
        q.data.salons.map((s) => <SalonCard key={s.id} s={s} open={p.location !== ALL} />)
      )}
    </main>
  );
}
